import { useEffect, useRef } from "react";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { AiBadge, AiSparkle } from "@/components/common/AiSparkle";
import { MiniMarkdown } from "./MiniMarkdown";
import { AskChips, AskForm, starterQuestions } from "./AskInputs";

const Typing = ({ status }) => (
  <span className="flex items-center gap-2 text-[13px] text-neutral-500" data-testid="ai-panel-status">
    <span className="ai-typing flex gap-1 text-brass"><span /><span /><span /></span> {status}
  </span>
);

const Bubble = ({ m, status, n }) => (m.role === "user" ? (
  <div className="ml-10 self-end rounded-2xl rounded-br-sm bg-ink px-4 py-2.5 text-[15px] text-white" data-testid={`ai-msg-user-${n}`}>{m.text}</div>
) : (
  <div className="mr-6 flex gap-2.5" data-testid={`ai-msg-assistant-${n}`}>
    <span className="mt-1 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-mist"><AiSparkle className="h-4 w-4" /></span>
    <div className={`min-w-0 rounded-2xl rounded-tl-sm px-4 py-2.5 text-[15px] leading-relaxed ${m.error ? "bg-red-50 text-red-700" : "bg-neutral-100 text-neutral-800"}`}>
      {m.pending && !m.text ? <Typing status={status || "Thinking…"} /> : <MiniMarkdown text={m.text} />}
    </div>
  </div>
));

const Allowance = ({ allow, onSignUp }) => {
  if (!allow || allow.signed_in) return null;
  const left = allow.remaining > 0;
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-neutral-200 bg-mist px-5 py-2.5 text-[13px] text-neutral-700" data-testid="ai-panel-allowance">
      <span>{left ? "Your first AI question is free. After that, a free account keeps it going." : "You've used your free AI try."}</span>
      {!left && <button type="button" onClick={onSignUp} className="font-medium text-ink underline underline-offset-4" data-testid="ai-panel-signup">Create free account</button>}
    </div>
  );
};

/** Zillow-style side panel: chat about one home (bases, schools, restaurants, VA loans). */
export const AssistantPanel = ({ listing, open, onOpenChange, chat, allow, onAsk, onSignUp }) => {
  const scroller = useRef(null);
  const { messages, status, busy } = chat;
  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, status, open]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" closeTestId="ai-panel-close" className="flex w-full flex-col gap-0 p-0 sm:max-w-md" data-testid="ai-panel">
        <div className="border-b border-neutral-200 px-5 pb-4 pt-5 pr-12">
          <div className="flex items-center gap-2"><AiSparkle className="h-6 w-6" /><SheetTitle className="text-lg font-medium">AI home assistant</SheetTitle><AiBadge /></div>
          <SheetDescription className="mt-1 text-[13px] text-neutral-500">{listing.title} · {listing.neighborhood}, {listing.city}</SheetDescription>
        </div>
        <Allowance allow={allow} onSignUp={onSignUp} />
        <div ref={scroller} className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-5" data-testid="ai-panel-messages">
          {messages.length === 0 && (
            <div data-testid="ai-panel-empty">
              <p className="ai-text font-display text-2xl">Ask me anything about this home.</p>
              <p className="mb-4 mt-2 text-[14px] text-neutral-600">Distances to every base, nearby schools and restaurants, VA loan questions and more.</p>
              <AskChips questions={starterQuestions(listing)} onAsk={onAsk} disabled={busy} testPrefix="ai-panel" />
            </div>
          )}
          {messages.map((m, i) => <Bubble key={i} m={m} status={status} n={messages.slice(0, i).filter((x) => x.role === m.role).length} />)}
        </div>
        <div className="border-t border-neutral-200 bg-white px-4 pb-4 pt-3">
          <AskForm onAsk={onAsk} busy={busy} testPrefix="ai-panel" />
          <p className="mt-2 text-center text-[12px] text-neutral-500">AI can make mistakes. Distances are straight-line. Places © OpenStreetMap contributors.</p>
        </div>
      </SheetContent>
    </Sheet>
  );
};
